import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2, Upload, Download, Trash2, FileText, Image as ImageIcon, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '@/lib/utils';

const CATS = [
  { key: 'id', label: 'ID' },
  { key: 'policy', label: 'Policy' },
  { key: 'application', label: 'Application' },
  { key: 'medical', label: 'Medical' },
  { key: 'other', label: 'Other' },
];

function fmtSize(n) {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

const isPreviewable = (mime) => (mime || '').startsWith('image/') || mime === 'application/pdf';

// Reusable attachments list used by both leads and clients. `docsApi`
// exposes list/upload/download/update/remove bound to the entity.
export function DocumentsSection({ entityId, docsApi, title = 'Documents', testPrefix = 'doc' }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [uploadCat, setUploadCat] = useState('other');
  const [preview, setPreview] = useState(null); // {url, mime, filename}
  const [previewLoading, setPreviewLoading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await docsApi.listDocuments(entityId);
      setRows(r.data?.documents || []);
    } catch (_) { /* no-op */ } finally { setLoading(false); }
  }, [entityId, docsApi]);

  useEffect(() => { load(); }, [load]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await docsApi.uploadDocument(entityId, file, uploadCat);
      toast.success('File uploaded');
      await load();
    } catch (err) { toast.error(getErrorMessage(err, 'Upload failed')); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const fetchBlob = async (d) => (await docsApi.downloadDocument(entityId, d.document_id)).data;

  const download = async (d) => {
    try {
      const blob = await fetchBlob(d);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const a = document.createElement('a');
      a.href = url; a.download = d.filename || 'document';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(getErrorMessage(err, 'Download failed')); }
  };

  const openPreview = async (d) => {
    setPreviewLoading(true);
    try {
      const blob = await fetchBlob(d);
      const url = window.URL.createObjectURL(new Blob([blob], { type: d.mime }));
      setPreview({ url, mime: d.mime, filename: d.filename });
    } catch (err) { toast.error(getErrorMessage(err, 'Could not open preview')); }
    finally { setPreviewLoading(false); }
  };

  const closePreview = () => {
    if (preview?.url) window.URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const changeCat = async (d, category) => {
    setRows((x) => x.map((r) => r.document_id === d.document_id ? { ...r, category } : r));
    try { await docsApi.updateDocument(entityId, d.document_id, category); }
    catch (err) { toast.error(getErrorMessage(err, 'Could not update category')); load(); }
  };

  const remove = async (d) => {
    try {
      await docsApi.deleteDocument(entityId, d.document_id);
      setRows((x) => x.filter((r) => r.document_id !== d.document_id));
    } catch (err) { toast.error(getErrorMessage(err, 'Delete failed')); }
  };

  const shown = filter === 'all' ? rows : rows.filter((d) => (d.category || 'other') === filter);
  const countFor = (k) => rows.filter((d) => (d.category || 'other') === k).length;

  return (
    <div className="space-y-3" data-testid={`${testPrefix}-section`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h4 className="text-sm font-semibold text-slate-700">{title}</h4>
        <div className="flex items-center gap-2">
          <select
            value={uploadCat}
            onChange={(e) => setUploadCat(e.target.value)}
            className="h-9 rounded-md border border-slate-200 bg-white px-2 text-xs"
            data-testid={`${testPrefix}-upload-category`}
          >
            {CATS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          <input ref={fileRef} type="file" className="hidden" onChange={onPick} data-testid={`${testPrefix}-file-input`} />
          <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid={`${testPrefix}-upload`}>
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-1" /> Upload</>}
          </Button>
        </div>
      </div>

      {/* Category filter chips */}
      <div className="flex items-center gap-1.5 flex-wrap" data-testid={`${testPrefix}-filters`}>
        <button type="button" onClick={() => setFilter('all')}
          className={`rounded-full px-2.5 py-0.5 text-xs border ${filter === 'all' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200'}`}
          data-testid={`${testPrefix}-filter-all`}>All ({rows.length})</button>
        {CATS.map((c) => (
          <button key={c.key} type="button" onClick={() => setFilter(c.key)}
            className={`rounded-full px-2.5 py-0.5 text-xs border ${filter === c.key ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200'}`}
            data-testid={`${testPrefix}-filter-${c.key}`}>{c.label} ({countFor(c.key)})</button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
      ) : shown.length === 0 ? (
        <p className="text-sm italic text-slate-400 py-4 text-center" data-testid={`${testPrefix}-empty`}>No files here.</p>
      ) : (
        <ul className="space-y-2">
          {shown.map((d) => {
            const img = (d.mime || '').startsWith('image/');
            return (
              <li key={d.document_id} className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2" data-testid={`${testPrefix}-${d.document_id}`}>
                {img ? <ImageIcon className="h-4 w-4 text-slate-400 shrink-0" /> : <FileText className="h-4 w-4 text-slate-400 shrink-0" />}
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-slate-800 truncate">{d.filename}</div>
                  <div className="text-[11px] text-slate-400">{fmtSize(d.size)}{d.created_at ? ` · ${new Date(d.created_at).toLocaleDateString()}` : ''}{d.copied_from ? ' · from lead' : ''}</div>
                </div>
                <select
                  value={d.category || 'other'}
                  onChange={(e) => changeCat(d, e.target.value)}
                  className="h-8 rounded-md border border-slate-200 bg-white px-1.5 text-xs"
                  title="Category"
                  data-testid={`${testPrefix}-cat-${d.document_id}`}
                >
                  {CATS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
                {isPreviewable(d.mime) && (
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openPreview(d)} disabled={previewLoading} data-testid={`${testPrefix}-preview-${d.document_id}`}><Eye className="h-4 w-4" /></Button>
                )}
                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => download(d)} data-testid={`${testPrefix}-download-${d.document_id}`}><Download className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600 hover:text-rose-700" onClick={() => remove(d)} data-testid={`${testPrefix}-delete-${d.document_id}`}><Trash2 className="h-4 w-4" /></Button>
              </li>
            );
          })}
        </ul>
      )}

      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) closePreview(); }}>
        <DialogContent className="max-w-3xl" data-testid={`${testPrefix}-preview-modal`}>
          <DialogHeader><DialogTitle className="text-base truncate pr-6">{preview?.filename}</DialogTitle></DialogHeader>
          {preview && (preview.mime === 'application/pdf' ? (
            <iframe title="preview" src={preview.url} className="w-full h-[70vh] rounded border border-slate-200" />
          ) : (
            <div className="flex justify-center bg-slate-50 rounded border border-slate-200 p-2">
              <img src={preview.url} alt={preview.filename} className="max-h-[70vh] object-contain" />
            </div>
          ))}
          <div className="flex justify-end">
            {preview && <a href={preview.url} download={preview.filename}><Button size="sm" variant="outline"><Download className="h-4 w-4 mr-1" /> Download</Button></a>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
